import {useEffect, useState} from 'react';
import {getIcrByNumber} from '../../../firebase/firestore/icr';
import {getIcrByNumber as getIcrByNumberTest} from '../../../features/collection-requests/_test_data/ICRequests';

/**
 * Hook to load and manage ICR data.
 * Handles both embedded ICR data and fetching by ICR number.
 *
 * @param {Object} params
 * @param {boolean} params.hasEmbeddedIcr - Whether embedded ICR data exists
 * @param {Object|null} params.embeddedIcrData - Pre-built embedded ICR data
 * @param {string|null} params.icrNumber - ICR number to fetch
 * @returns {Object} ICR data and loading state
 */
export const useIcrData = ({hasEmbeddedIcr, embeddedIcrData, icrNumber}) => {
    const [icr, setIcr] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Priority 1: Use embedded ICR directly
        if (hasEmbeddedIcr && embeddedIcrData) {
            setIcr(embeddedIcrData);
            setIsLoading(false);
            setError(null);
            return;
        }

        // Priority 2: Fetch by ICR number
        if (icrNumber) {
            let cancelled = false;
            const decodedNumber = decodeURIComponent(icrNumber);

            const fetchIcr = async () => {
                setIsLoading(true);
                setError(null);

                try {
                    const result = await getIcrByNumber(decodedNumber);
                    if (!cancelled) {
                        // Fallback to test data if no result
                        setIcr(result || getIcrByNumberTest(decodedNumber));
                    }
                } catch (err) {
                    if (!cancelled) {
                        console.error('Failed to fetch ICR:', err);
                        setError(err);
                        // Try test data as fallback
                        setIcr(getIcrByNumberTest(decodedNumber));
                    }
                } finally {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                }
            };

            fetchIcr();
            return () => {
                cancelled = true;
            };
        }

        // No ICR data available
        setIcr(null);
        setIsLoading(false);
        setError(null);
    }, [hasEmbeddedIcr, embeddedIcrData, icrNumber]);

    return {icr, isLoading, error};
};
